import { useEffect, useRef, useState } from "react";
import { beep, enableSound } from "./beeps";
import { useCapture } from "./useCapture";

/**
 * One photograph, one field: the name.
 *
 * This replaces the standalone scan flow. That version was a separate way into
 * the app that produced a *draft* of an item, which meant two routes to the same
 * screen and a confirm step that existed only because the camera had guessed.
 *
 * Now the camera is a way of filling in a field you are already looking at:
 * you're on the add screen, you tap the camera beside "name", you get a name.
 * The form is the one source of truth and everything in it is editable, so the
 * "check this before saving" screen stops being a separate concept — it is just
 * the form you were already on.
 *
 * There used to be a second shot for the printed date. It is gone (ADR 0017):
 * identifying a product from the front of a pack is what the camera is good at,
 * and a date is faster and more reliable as a tap on a chip. The extraction
 * pipeline still reads dates — the sweep and `Need` are untouched — this screen
 * simply no longer asks it to.
 */

export interface Caught {
  productName: string | null;
  brand: string | null;
  sizeText: string | null;
}

export function CaptureSheet({
  timezone,
  onCaught,
  onClose,
}: {
  timezone: string;
  onCaught: (found: Caught) => void;
  onClose: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [camError, setCamError] = useState<string | null>(null);
  const [missed, setMissed] = useState(false);

  const cam = useCapture(video, timezone);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;

    navigator.mediaDevices
      // The back camera, and as much resolution as it will give us. The 1568px
      // floor (ADR 0004) still applies: it was measured on dates, but small
      // print is small print, and a misread brand is a misread brand.
      ?.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      })
      .then((s) => {
        if (cancelled) {
          for (const t of s.getTracks()) t.stop();
          return;
        }
        stream = s;
        if (video.current) {
          video.current.srcObject = s;
          void video.current.play();
        }
      })
      .catch((e: Error) => setCamError(cameraMessage(e)));

    // Releasing the camera on unmount is not optional. Leaving the light on
    // after the user left the screen was a real complaint: "I closed the app,
    // there was no way to stop the camera".
    return () => {
      cancelled = true;
      for (const t of stream?.getTracks() ?? []) t.stop();
    };
  }, []);

  async function shoot() {
    enableSound();
    setMissed(false);

    const r = await cam.shoot("name", null);
    if (!r) return;
    if (!r.productName) {
      setMissed(true);
      return;
    }
    beep("name");
    onCaught({
      productName: r.productName,
      brand: r.brand,
      sizeText: r.sizeText,
    });
  }

  const note =
    cam.error ??
    (missed
      ? "Couldn't read a name there. Try the front of the pack."
      : cam.busy
        ? "Reading…"
        : cam.steady
          ? "Looks sharp — take the shot."
          : "Hold steady, or move a little closer.");

  return (
    <section aria-label="scan a label" className="scanfull">
      <header className="scanbar">
        <button type="button" className="scanback" onClick={onClose} aria-label="Back">
          ✕
        </button>
        <p className="scantitle">Scan the name</p>
      </header>

      {camError ? (
        <div className="scanbody">
          <div className="hint" data-testid="camera-error">
            <strong>No camera</strong>
            <p>{camError}</p>
            <p className="dim">You can still type it in — nothing is blocked.</p>
          </div>
        </div>
      ) : (
        <div className="viewport">
          {/* A live camera preview has no audio track and nothing to caption. */}
          <video
            ref={video}
            className={`viewfeed${cam.busy ? " busy" : ""}`}
            playsInline
            muted
            data-testid="viewfinder"
          />
          <p className="shot-prompt" data-testid="scan-prompt">
            Point at the front of the pack
          </p>
        </div>
      )}

      {/* A word under the viewfinder was not enough. People read the unchanged
          preview as "the button didn't work" and pressed again — and again —
          while a request was already in flight. The shutter was disabled, so
          nothing happened, which only confirmed the theory.

          So the wait gets the whole screen: it covers the shutter rather than
          greying it out, it says how long this takes, and it swallows the taps
          that used to land on nothing. */}
      {cam.busy && (
        <div
          className="scan-working"
          role="status"
          aria-live="assertive"
          data-testid="scan-working"
        >
          <span className="spinner on-dark" aria-hidden="true" />
          <p className="working-label">Reading the label…</p>
          <p className="working-hint">
            This takes a few seconds. No need to press again — hold on.
          </p>
        </div>
      )}

      <footer className="scanfoot">
        {!camError && (
          <>
            <div className="focusbar" aria-hidden="true">
              <div
                className={`focusfill ${cam.steady ? "good" : ""}`}
                style={{ width: `${Math.round(cam.focus * 100)}%` }}
              />
            </div>
            <p
              className={`shot-note${cam.error || missed ? " warn" : ""}`}
              data-testid="focus-hint"
            >
              {note}
            </p>
          </>
        )}

        <div className="shotrow">
          <button
            type="button"
            className="ghost small-action"
            onClick={onClose}
            data-testid="scan-give-up"
          >
            {camError ? "Type it instead" : "Cancel"}
          </button>

          {!camError && (
            <button
              type="button"
              className="shutter"
              onClick={shoot}
              disabled={cam.busy}
              aria-label="Capture the name"
              data-testid="shutter"
            >
              <span className="shutter-ring" />
            </button>
          )}
        </div>
      </footer>
    </section>
  );
}

function cameraMessage(e: Error): string {
  if (e.name === "NotAllowedError") return "Camera permission was denied.";
  if (e.name === "NotFoundError") return "No camera on this device.";
  // The common one in development: getUserMedia needs a secure origin, and a
  // LAN IP over plain HTTP is not one. See ADR 0006.
  if (!window.isSecureContext) return "The camera needs HTTPS. Open the https:// URL.";
  return e.message;
}
